import * as React from 'react'

import { observer } from 'mobx-react'

import EditableTextColumn from '~/client/src/desktop/components/EditableColumns/EditableTextColumn/EditableTextColumn'
import MaterialStatusIndicator from '~/client/src/desktop/components/MaterialStatusIndicator/MaterialStatusIndicator'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import { LWFCRowData } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import VirtualMultiGrid from '~/client/src/shared/components/ListWithFixedColumns/VirtualMultiGrid'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import Location from '~/client/src/shared/components/SitemapAttributeSelector/Location'
import { MaterialStatus } from '~/client/src/shared/enums/MaterialStatus'
import Company from '~/client/src/shared/models/Company'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { DASH, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import CurrentLocationCell from './components/CurrentLocationCell'
import DeliveryDataCell from './components/DeliveryDataCell'
import MaterialTransferCell from './components/MaterialTransferCell'
import { MaterialsDataKeys } from './MaterialsDataKeys'
import MaterialsListStore, {
  IEditableCellModel,
  IFormCellData,
  ILocationCellData,
} from './MaterialsList.store'

interface IMaterialsList {
  store: MaterialsListStore
}

const LOCATION_ICON_SIZE = 14

@observer
export default class MaterialsList extends React.Component<IMaterialsList> {
  public render() {
    const {
      rows,
      columns,
      columnsWidthState,
      collapsedCategories,
      toggleCategoryCollapsing,
      fixedColumnsCount,
      sortState,
      handleColumnSort,
      onSelectAll,
      onResetAllSelections,
      toggleCategory,
    } = this.props.store

    return (
      <VirtualMultiGrid
        rows={rows.slice(1)}
        columns={columns}
        fixedColumnsCount={fixedColumnsCount}
        cellRenderer={this.cellRenderer}
        onCellClick={this.handleCellClick}
        collapsedCategories={collapsedCategories}
        onCategoryCollapsingToggle={toggleCategoryCollapsing}
        onColumnSort={handleColumnSort}
        sortedColumnKey={sortState.columnKey}
        sortingOrder={sortState.order}
        columnsWidthState={columnsWidthState}
        selectAll={onSelectAll}
        resetAll={onResetAllSelections}
        onCategoryCheckboxToggle={toggleCategory}
      />
    )
  }

  private handleCellClick = (rowData: LWFCRowData, columnKey: string) => {
    const { onCheckboxClick } = this.props.store
    if (columnKey === MaterialsDataKeys.CHECKBOX) {
      onCheckboxClick(rowData[MaterialsDataKeys.ID])
    }
  }

  private cellRenderer = (columnKey: string, value: any) => {
    const renderers = this.columnKeyToCellRenderer
    if (renderers[columnKey]) {
      return (
        <div className="text-ellipsis px12">{renderers[columnKey](value)}</div>
      )
    }
    return <div className="text-ellipsis px12">{String(value || DASH)}</div>
  }

  private get columnKeyToCellRenderer() {
    return {
      [MaterialsDataKeys.CHECKBOX]: (value: boolean) => (
        <span className="tw:flex tw:justify-center tw:w-full">
          <Checkbox checked={value} />
        </span>
      ),
      [MaterialsDataKeys.STATUS]: (value: MaterialStatus) => (
        <MaterialStatusIndicator status={value} />
      ),
      [MaterialsDataKeys.NAME]: this.renderEditableTextColumn,
      [MaterialsDataKeys.DESCRIPTION]: this.renderEditableTextColumn,
      [MaterialsDataKeys.PLANNED_QUANTITY]: this.renderEditableTextColumn,
      [MaterialsDataKeys.PLANNED_INSTALL_LOCATION]: this.renderLocation,
      [MaterialsDataKeys.PLANNED_DELIVERY_LOCATION]: this.renderLocation,
      [MaterialsDataKeys.NEXT_DELIVERY]: this.renderDeliveryDataCell(
        MaterialsDataKeys.NEXT_DELIVERY,
      ),
      [MaterialsDataKeys.NEXT_DELIVERY_BOOKING_DATE]:
        this.renderDeliveryDataCell(
          MaterialsDataKeys.NEXT_DELIVERY_BOOKING_DATE,
        ),
      [MaterialsDataKeys.CURRENT_MATERIAL_LOCATION]:
        this.renderCurrentLocationCell,
      [MaterialsDataKeys.VENDOR]: this.renderDeliveryDataCell(
        MaterialsDataKeys.VENDOR,
      ),
      [MaterialsDataKeys.RESPONSIBLE_COMPANY]: this.renderDeliveryDataCell(
        MaterialsDataKeys.RESPONSIBLE_COMPANY,
      ),
      [MaterialsDataKeys.MATERIAL_TRANSFER_ID]: this.renderMaterialTransferCell(
        MaterialsDataKeys.MATERIAL_TRANSFER_ID,
      ),
      [MaterialsDataKeys.MATERIAL_TRANSFER_DATE]:
        this.renderMaterialTransferCell(
          MaterialsDataKeys.MATERIAL_TRANSFER_DATE,
        ),
    }
  }

  private renderEditableTextColumn = ({
    viewModelId,
    value,
    isNumber,
    onChange,
  }: IEditableCellModel): JSX.Element => {
    const inputType = isNumber ? 'number' : null
    return (
      <EditableTextColumn
        value={value}
        onSave={onChange}
        isEditable
        type={inputType}
        rowId={viewModelId}
      />
    )
  }

  private renderLocation(location: LocationBase): JSX.Element {
    if (!location) {
      return <div>{NO_VALUE}</div>
    }

    return (
      <div className="col y-center full-height text-ellipsis">
        <Location
          dto={location}
          iconSize={LOCATION_ICON_SIZE}
          contentClassName="text large"
          chainClassName="text small grey-light ellipsis pl24"
          contentContainerClassName="text-ellipsis py2"
        />
      </div>
    )
  }

  private renderDeliveryDataCell =
    (dataKey: string) =>
    (cellData: IFormCellData): JSX.Element => (
      <DeliveryDataCell
        dataKey={dataKey}
        deliveryCellData={cellData}
        onNewDeliveryClick={this.props.store.openNewFormFromCell}
        onIdClick={this.props.store.openExistingDelivery}
        isNewBtnAvailable={!this.props.store.areMaterialsHiddenInDelivery}
        locationRenderer={this.renderLocation}
        companyRenderer={this.renderCompanyProfile}
      />
    )

  private renderCurrentLocationCell = (
    cellData: ILocationCellData,
  ): JSX.Element => (
    <CurrentLocationCell
      locationCellData={cellData}
      isTransferEnabled={!!this.props.store.transferFormType}
      onNewTransferClick={this.props.store.openNewTransferForm}
      onTransferIdClick={this.props.store.openExistingTransfer}
      locationRenderer={this.renderLocation}
      transferFormIconName={this.props.store.transferFormIconName}
    />
  )

  private renderMaterialTransferCell =
    (dataKey: string) =>
    (cellData: IFormCellData): JSX.Element => (
      <MaterialTransferCell
        dataKey={dataKey}
        formCellData={cellData}
        isTransferEnabled={!!this.props.store.transferFormType}
        onNewTransferClick={this.props.store.openNewTransferForm}
        onTransferIdClick={this.props.store.openExistingTransfer}
        locationRenderer={this.renderLocation}
        transferFormIconName={this.props.store.transferFormIconName}
      />
    )

  private renderCompanyProfile(company: Company): JSX.Element {
    if (!company) {
      return <div>{NO_VALUE}</div>
    }

    return (
      <CompanyProfilePreview
        company={company}
        isMenuHidden={true}
        isPhoneHidden={true}
      />
    )
  }
}
